import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable, reaction} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {PlayerUtils} from "data/utils/player_utils.utils";
import {map} from "lodash";
import React, {RefObject, useRef} from "react";

export enum ScrollSide {
	TOP = "top",
	DOWN = "down",
	BOTH = "both",
	NONE = "none",
}

interface IScoreRow {
	name: string;
	value: number;
}

export interface IModalScoringController extends ViewController {
	close: () => void;
	onScroll: (currentTarget: HTMLDivElement) => void;

	get scrollRef(): RefObject<HTMLDivElement>;
	get i18n(): ILocalizationStore;

	get scrollSide(): ScrollSide;
	get isOpen(): boolean;

	get hittersScoringRows(): IScoreRow[];

	get pitchersScoringRows(): IScoreRow[];
}

@injectable()
export class ModalScoringController implements IModalScoringController {
	@observable _scrollSide = ScrollSide.NONE;
	private _scrollRef: RefObject<HTMLDivElement> = useRef(null);
	private _scrollDisposer: ReturnType<typeof reaction> | null = null;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get scrollRef(): React.RefObject<HTMLDivElement> {
		return this._scrollRef;
	}

	get scrollSide(): ScrollSide {
		return this._scrollSide;
	}

	get hittersScoringRows(): IScoreRow[] {
		return map(PlayerUtils.statsPoints.hitting, (points, key) => {
			return {
				name: this.i18n.t(`scoring.hitters.${key}`, key),
				value: points,
			};
		});
		// return [
		// 	{name: this.i18n.t("scoring.hitters.single", "Single"), value: 2},
		// 	{name: this.i18n.t("scoring.hitters.double", "Double"), value: 4},
		// 	{name: this.i18n.t("scoring.hitters.triple", "Triple"), value: 6},
		// 	{name: this.i18n.t("scoring.hitters.home_run", "Home Run"), value: 8},
		// 	{name: this.i18n.t("scoring.hitters.steal", "Steal"), value: 4},
		// 	{name: this.i18n.t("scoring.hitters.walk", "Walk"), value: 2},
		// 	{name: this.i18n.t("scoring.hitters.rbi", "RBI"), value: 4},
		// 	{name: this.i18n.t("scoring.hitters.run", "Run"), value: 4},
		// 	{name: this.i18n.t("scoring.hitters.hbp", "HBP"), value: 1},
		// 	{name: this.i18n.t("scoring.hitters.cycle", "Cycle"), value: 10},
		// 	{name: this.i18n.t("scoring.hitters.grand_slam", "Grand Slam"), value: 5},
		// 	{name: this.i18n.t("scoring.hitters.strikeout", "Strikeout"), value: -1},
		// 	{name: this.i18n.t("scoring.hitters.caught_stealing", "Caught stealing"), value: -2},
		// 	{name: this.i18n.t("scoring.hitters.gidp", "GIDP"), value: -3},
		// ];
	}
	get pitchersScoringRows(): IScoreRow[] {
		return map(PlayerUtils.statsPoints.pitching, (points, key) => {
			return {
				name: this.i18n.t(`scoring.pitcher.${key}`, key),
				value: points,
			};
		});
		// return [
		// 	{name: this.i18n.t("scoring.pitcher.out", "Out"), value: 1},
		// 	{name: this.i18n.t("scoring.pitcher.strikeout", "Strikeout"), value: 1},
		// 	{name: this.i18n.t("scoring.pitcher.scoreless_inning", "Scoreless Inning"), value: 1},
		// 	{
		// 		name: this.i18n.t("scoring.pitcher.successful_pick_off", "Successful Pick off"),
		// 		value: 3,
		// 	},
		// 	{name: this.i18n.t("scoring.pitcher.win", "Win"), value: 5},
		// 	{name: this.i18n.t("scoring.pitcher.quality_start", "Quality Start"), value: 8},
		// 	{name: this.i18n.t("scoring.pitcher.complete_game", "Complete game"), value: 10},
		// 	{name: this.i18n.t("scoring.pitcher.cg_shutout", "CG Shutout"), value: 15},
		// 	{name: this.i18n.t("scoring.pitcher.no_hitter", "No-hitter"), value: 25},
		// 	{name: this.i18n.t("scoring.pitcher.perfect_game", "Perfect Game"), value: 50},
		// 	{name: this.i18n.t("scoring.pitcher.hits_allowed", "Hits allowed"), value: -1},
		// 	{name: this.i18n.t("scoring.pitcher.earned_runs", "Earned runs"), value: -2},
		// 	{name: this.i18n.t("scoring.pitcher.walks", "Walks"), value: -2},
		// 	{name: this.i18n.t("scoring.pitcher.batter_hit", "Batter hit"), value: -1},
		// 	{name: this.i18n.t("scoring.pitcher.losses", "Losses"), value: -5},
		// 	{name: this.i18n.t("scoring.pitcher.wild_pitch", "Wild Pitch"), value: -1},
		// ];
	}

	onScroll = (currentTarget: HTMLDivElement) => {
		const {scrollTop, clientHeight, scrollHeight} = currentTarget;
		if (clientHeight === scrollHeight) {
			this._scrollSide = ScrollSide.NONE;
			return;
		}

		if (scrollTop > scrollHeight - clientHeight - 100) {
			this._scrollSide = ScrollSide.TOP;
			return;
		}

		if (scrollTop < 40) {
			this._scrollSide = ScrollSide.DOWN;
			return;
		}

		if (scrollTop <= scrollHeight - clientHeight - 100 && scrollTop > 40) {
			this._scrollSide = ScrollSide.BOTH;
		}
	};

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.SCORING;
	}
	public close = () => {
		this._modalsStore.hideModal();
	};

	dispose(): void {
		this._scrollDisposer?.();
	}

	init(): void {
		this._scrollDisposer = reaction(
			() => this.isOpen,
			() => {
				const elem = this.scrollRef.current;
				if (elem) {
					this.onScroll(elem);
				}
			},
			{
				delay: 300,
			}
		);
	}

	onChange(param: void): void {
		return;
	}
}
